import React from "react";

export default function BlogPickChicoAreaCFast() {
  const UPDATED = "2025-08-25";
  const CANONICAL = "https://www.graduationscope.com/blog/pick-chico-area-c-fast";

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: "How to pick Chico State Area C (Arts & Humanities) in 10 minutes",
    datePublished: "2025-08-25",
    dateModified: UPDATED,
    author: { "@type": "Organization", name: "GraduationScope" },
    publisher: {
      "@type": "Organization",
      name: "GraduationScope",
      logo: { "@type": "ImageObject", url: "https://www.graduationscope.com/graduation-scope-logo.png" }
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": CANONICAL }
  };

  return (
    <main className="post-wrap">
      {/* React 19 hoists these into <head> */}
      <title>How to Pick Chico State Area C (Arts & Humanities) in 10 Minutes | GraduationScope</title>
      <meta
        name="description"
        content="Plan Chico State Area C quickly: know C1 vs C2, use Catalog lists, balance workload, and verify tags. TL;DR, table, and official sources."
      />
      <link rel="canonical" href={CANONICAL} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />

      <style>{`
        .post-wrap{max-width:980px;margin:0 auto;padding:120px 24px 40px}
        .post-h1{font-size:1.9rem;font-weight:800;margin:8px 0 16px}
        .muted{color:#555}
        .tldr{background:#f8f9fb;border:1px solid #e7e7e7;border-radius:12px;padding:12px 14px;margin:12px 0 18px}
        .tldr ul{margin:0;padding-left:18px}
        .tldr li{margin:4px 0}
        h2{margin-top:18px}
        a{color:#1a56db;text-decoration:underline}
        table{width:100%;border-collapse:collapse;margin:12px 0 18px}
        th,td{border:1px solid #e2e2e2;padding:10px;vertical-align:top}
        th{background:#fafafa;text-align:left;font-weight:700}
        .capsule{background:#fffdf7;border:1px solid #efe6c7;border-radius:12px;padding:14px;margin:12px 0 18px}
      `}</style>

      <h1 className="post-h1">How to Pick Chico State Area C (Arts & Humanities) in 10 Minutes</h1>
      <p className="muted">Updated on {UPDATED}</p>

      <section className="tldr">
        <strong>TL;DR</strong>
        <ul>
          <li>Area C = <strong>C1 Arts</strong> + <strong>C2 Humanities</strong>; plan at least one from each.</li>
          <li>Use the Chico State Catalog to confirm which courses currently carry C1/C2.</li>
          <li>Balance one “approachable” C course with heavier major work.</li>
          <li>Verify your catalog year and degree progress before enrolling.</li>
        </ul>
      </section>

      <h2>Answer Capsule</h2>
      <div className="capsule">
        <p>
          Start by checking your degree progress to see which GE areas—specifically C1 and C2—are still open. Then use the
          <strong> Chico State Catalog</strong> to confirm the latest C1/C2 course lists for your catalog year. When you search the
          current Class Schedule, verify the GE tag on the exact section you intend to take. Build a balanced term by pairing
          one “approachable” C course with heavier major or lab classes. Policies and availability can change, so confirm against
          official pages each term.
        </p>
      </div>

      <h2>Quotable Table (examples)</h2>
      <p className="muted">Examples only; verify C1/C2 status in the Catalog for your catalog year and term.</p>
      <table>
        <thead>
          <tr>
            <th>Course</th>
            <th>Subarea</th>
            <th>Why it may feel approachable</th>
            <th>Typical work</th>
            <th>Official link</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>MUSC 290 — World Music</td>
            <td>C1 Arts</td>
            <td>Survey/listening focus; global perspectives</td>
            <td>Listening logs, short quizzes</td>
            <td><a href="https://catalog.csuchico.edu/courses/musc/" target="_blank" rel="nofollow noopener">Catalog — MUSC</a></td>
          </tr>
          <tr>
            <td>THEA 110 — Introduction to Theatre Arts</td>
            <td>C1 Arts</td>
            <td>Intro survey; performance literacy</td>
            <td>Responses, participation, exams</td>
            <td><a href="https://catalog.csuchico.edu/courses/thea/" target="_blank" rel="nofollow noopener">Catalog — THEA</a></td>
          </tr>
          <tr>
            <td>HUMN 222W — Arts & Ideas: Renaissance to Present</td>
            <td>C2 Humanities</td>
            <td>Thematic readings; writing prompts</td>
            <td>Short papers, reading checks</td>
            <td><a href="https://catalog.csuchico.edu/colleges-departments/college-humanities-fine-arts/humanities/humanities-ba/humanities-ba.pdf" target="_blank" rel="nofollow noopener">Catalog PDF</a></td>
          </tr>
          <tr>
            <td>PHIL 110 — Introduction to Philosophy</td>
            <td>C2 Humanities</td>
            <td>Conceptual reasoning; structured readings</td>
            <td>Reading responses, essays</td>
            <td><a href="https://catalog.csuchico.edu/courses/phil/" target="_blank" rel="nofollow noopener">Catalog — PHIL</a></td>
          </tr>
        </tbody>
      </table>

      <h2>Check C1/C2 in your plan</h2>
      <p>Use your degree progress tools to see which C subareas are still open. Confirm your catalog year and any limits.</p>

      <h2>Use the Catalog & Schedule</h2>
      <p>Open the Catalog lists for C1 and C2, then verify the GE tag on the exact section in the current Class Schedule.</p>

      <h2>Balance light vs heavy</h2>
      <p>Pair one “approachable” C course with major/lab work. Read official descriptions and the syllabus (when available) to gauge workload.</p>

      <h2>Internal links</h2>
      <ul>
        <li><a href="/guides/chico-easiest-classes">Chico: Easiest Classes (guide)</a></li>
        <li><a href="/faq/chico">Chico FAQ</a></li>
        <li><a href="/chico">Chico Tracker</a></li>
      </ul>

      <h2>Sources</h2>
      <ol>
        <li><a href="https://catalog.csuchico.edu/colleges-departments/undergraduate-education-academic-success/general-education/" target="_blank" rel="nofollow noopener">Chico State — General Education (Catalog)</a></li>
        <li><a href="https://catalog.csuchico.edu/colleges-departments/undergraduate-education-academic-success/general-education/general-education.pdf" target="_blank" rel="nofollow noopener">GE course lists (PDF)</a></li>
        <li><a href="https://catalog.csuchico.edu/courses/musc/" target="_blank" rel="nofollow noopener">Catalog — MUSC</a></li>
        <li><a href="https://catalog.csuchico.edu/courses/thea/" target="_blank" rel="nofollow noopener">Catalog — THEA</a></li>
        <li><a href="https://catalog.csuchico.edu/courses/phil/" target="_blank" rel="nofollow noopener">Catalog — PHIL</a></li>
      </ol>
    </main>
  );
}
